import React, { useEffect, useRef, useState } from "react";
import { useSpring, a, config } from "react-spring";
import styles from "./poicards.module.css";
import { FormattedMessage, useIntl } from "react-intl";
import { map } from "../../mapbox/map.sideEffects/useMap.sideEffects.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  CAMPUS_COLLECTION,
  NAVI_PRIORITY_LIST,
  POIS,
} from "../../../../reduxStore/actions/main.js";
import { zoomBuildingsEnd } from "../../mapbox/functions/filter.map.js";
import StandardButton from "../../../elements/buttons/standard.button.jsx";
import StandardCard from "../../../elements/cards/StandardCard.card.jsx";
import { layerIds } from "../../mapbox/variables/layerIds.js";
import { isMobile } from "react-device-detect";
import { CUSTOMIZABLE_VALUES } from "../../mapbox/variables/CUSTOMIZABLE_VALUES.js";
import { statisticLogger } from "../../../statisticLogger/StatisticLogger.container";

/**
 * Das sind die Karten die von unten eingeblendet werden. Sie zeigen die momentan gerenderten POIs
 * an. Klickt man auf eine der POICards öffnet diese eine InfoCard, welche von unten halb hochfährt!
 * @params {boolean} supposedToShow - wenn false, sollen die Cards geschlossen werden
 * @returns {JSX.Element}
 */
export default function POICards({
  mapLoaded = false,
  onClosingEnd,
  currentFeature,
  setCurrentFeature,
  setPreOpen,
  supposedToShow,
  currentChildrenPOIs,
  currentCampusId,
  mapBounds,
}) {
  const [visibileFeatures, setVisibileFeatures] = useState([]);
  const [springProps, springAPI] = useSpring(() => ({
    from: {
      opacity: 0,
      transform: "translateY(500px)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0px)",
    },
    config: config.stiff,
  }));
  const poiCardContainer = useRef(null);
  const veryImportantPois = useRef([]);
  useEffect(() => {
    const vips = NAVI_PRIORITY_LIST.map((poiId) => {
      // somethings it does not find a poi!
      // return POIS.all.find((poi) => poi.id === poiId);
      return POIS.all_objects[poiId];
    });
    veryImportantPois.current = vips.filter((poi) => {
      return poi?.campusId === currentCampusId;
    });
  }, [currentCampusId]);
  const handleSlideDown = () => {
    springAPI.start({
      // y: poiCardContainer.current.clientHeight + window.innerHeight / 5,
      opacity: 0,
      transform: "translate(200px)",
      immediate: false,
      config: { ...config.stiff },
      onRest: onClosingEnd,
    });
  };
  // todo: das wird eigentlich garnicht benötigt
  const handleSlideUp = () => {
    springAPI.start({
      to: {
        opacity: 1,
        transform: "translateY(0px)",
      },
      config: { ...config.stiff },
    });
  };

  /**
   * Bei Polygons verteilt Mapbox intern die Features auf mehrere Tilesets,
   * dadurch können Duplikate herauskommen, die ausgefiltert werden müssen.
   * @param array
   * @return {*[]}
   */
  const removeDuplicates = (array) => {
    const noDuplicates = [...array];

    noDuplicates.forEach((item, index) => {
      for (let j = index + 1; j < noDuplicates.length; j++) {
        let subitem = noDuplicates[j];
        if (item.id === subitem.id) {
          noDuplicates.splice(j, 1);
          j = j - 1;
        }
      }
    });
    return noDuplicates;
  };

  const getOtherCampuses = () => {
    let firstCampus = null;
    // alle campuses außer das ausgewählte
    let campusCards = CAMPUS_COLLECTION.getAllCampuses().map((c) => {
      let campusPoi = c.getPOI();
      let card = (
        <PreviewPoiCard
          key={campusPoi.id + "Campus"}
          icon={["fal", "city"]}
          headline={campusPoi.short ? campusPoi.short : campusPoi.name}
          feature={campusPoi}
          text={campusPoi.address}
          onDivClick={handleCardClick}
          onInfoClick={handleInfoClick}
        />
      );

      if (c.id === currentCampusId) {
        firstCampus = card;
      } else {
        return card;
      }
    });

    if (firstCampus) {
      campusCards.unshift(firstCampus);
    }
    return campusCards;
  };
  /** Markiert nur das Feature auf der Map */
  const handleCardClick = (feature) => {
    if (feature) {
      setCurrentFeature(feature);
      statisticLogger.addLog({
        action: {
          id: "cardselectfrompoicards",
          name: "Select card from shown poicards",
          group: "Map",
          movement: "stay",
          type: "click",
          interaction: "select",
          content: {
            globalPoiIds: [feature.globalPoiId],
            globalPoiId: feature.globalPoiId,
          },
        },
      });
      if (!isMobile) {
        // bei terminal sollen auch normal ausgewählte karten sofort die POICard öffnen
        setPreOpen();
      }
    }
  };
  /** markiert das angeklickte Feature und öffnet eine Feature Card */
  const handleInfoClick = (feature) => {
    setCurrentFeature(feature);
    // Öffnet die BigCard (InfoCard)
    setPreOpen();
  };
  /**
   * Iteriert Features (die auf der Map gerendert sind) und baut die POICards
   */
  const getVisiblePOICards = () => {
    let featuresToUse = [];
    if (currentChildrenPOIs) {
      // wenn grad die Kinder aktiv sind, sollen nur diese angezeigt werden!
      currentChildrenPOIs.forEach((cF) => {
        if (cF.hasLocation()) {
          featuresToUse.push(cF);
        }
      });
    } else {
      featuresToUse = visibileFeatures;
    }
    let poiCards = [];

    // Falls ein bestimmtes Zoomlevel erreicht ist, dann sollen auch nicht ausgewählte campusse angezeigt werden
    if (map.getZoom() < zoomBuildingsEnd) {
      poiCards = getOtherCampuses();
    }

    // falls ein currentFeature (und einen Standort hat) existiert soll es angezeigt werden, auch wenn es gerade nicht
    // gerendert ist!
    if (currentFeature && currentFeature.hasLocation()) {
      // POICard mit dem ausgewählten Feature muss nach vorne
      poiCards.unshift(
        <PreviewPoiCard
          key={currentFeature.id + "selected"}
          feature={currentFeature}
          headline={
            currentFeature.short ? currentFeature.short : currentFeature.name
          }
          text={currentFeature.address}
          selected={true}
          onDivClick={handleCardClick}
          onInfoClick={handleInfoClick}
        />
      );
    }

    try {
      featuresToUse.sort(function (a, b) {
        return b.properties.priority - a.properties.priority;
      });
    } catch (e) {
      console.log("Sorting by POI priority failed, no priority field.");
    }

    console.log(veryImportantPois.current);
    veryImportantPois.current.forEach((vipoi) => {
      if (
        vipoi &&
        vipoi.hasLocation() &&
        // auch in den vips darf natürlich nicht das ausgewählte feature nochmal zu sehen sein!
        (currentFeature?.id !== vipoi.id || !currentFeature)
      ) {
        poiCards.push(
          <PreviewPoiCard
            key={vipoi.id}
            feature={vipoi}
            headline={vipoi.short ? vipoi.short : vipoi.name}
            text={vipoi.address}
            onDivClick={handleCardClick}
            onInfoClick={handleInfoClick}
          />
        );
      }
    });

    featuresToUse = featuresToUse.filter((f) => {
      if (!f.properties || !f.properties.additionalData) return false;

      const additionalData = JSON.parse(f.properties.additionalData);
      return !!additionalData.highlight;
    });

    // Er soll durch alle Feature iterieren, aber nur 16 rendern
    let limit = 15;
    for (let i = 0; i < featuresToUse.length && i < limit; i++) {
      let feature = featuresToUse[i];
      let featureProps = currentChildrenPOIs ? feature : feature?.properties;

      if (
        featureProps &&
        feature.id !== currentFeature?.id &&
        NAVI_PRIORITY_LIST.indexOf(featureProps.id) === -1
      ) {
        poiCards.push(
          <PreviewPoiCard
            key={feature.id + "visible"}
            children={currentChildrenPOIs}
            feature={feature}
            headline={
              featureProps.short ? featureProps.short : featureProps.name
            }
            text={featureProps.address}
            selected={false}
            onDivClick={handleCardClick}
            onInfoClick={handleInfoClick}
          />
        );
      }
    }

    return poiCards;
  };

  useEffect(() => {
    /**
     * Checkt die Features, die auf der Map zu sehen sind
     */
    if (!mapLoaded) return;
    // Features die visible: hidden sind, werden automatisch nicht beachtet!
    let queriedFeatures = map.queryRenderedFeatures(null, {
      layers: [layerIds.texts.einrichtungen, layerIds.nodes.symbols],
    });
    setVisibileFeatures(removeDuplicates(queriedFeatures));
  }, [mapBounds]);

  useEffect(() => {
    setTimeout(() => {
      const container = document.getElementsByClassName(
        "show-poi-cards-container"
      )[0];
      if (container) {
        const containerWidth = container.clientWidth;
        const contentWidth = [...container.childNodes[0].childNodes].reduce(
          (result, child) => {
            return (result += child.clientWidth);
          },
          0
        );

        if (contentWidth < containerWidth) {
          container.classList.add("content-not-scroll");
        } else {
          container.classList.remove("content-not-scroll");
          poiCardContainer.current.addEventListener("wheel", (evt) => {
            if (evt.deltaY < 0 || evt.deltaY > 0) {
              poiCardContainer.current.scrollLeft += evt.wheelDelta;
            }
          });
        }
      }
    }, 500);
  }, [visibileFeatures]);

  // jedes mal wenn ein neues Feature ausgewählt wird, egal von wo, scrollen die poicards wieder zurück!
  useEffect(() => {
    if (currentFeature)
      poiCardContainer.current.scroll({
        left: 0,
        top: 0,
        behavior: "smooth",
      });
  }, [currentFeature]);
  useEffect(() => {
    if (!mapLoaded) return;

    if (supposedToShow) {
      handleSlideUp();
    } else {
      handleSlideDown();
    }
  }, [supposedToShow, mapLoaded]);

  return (
    <a.div
      className={`${styles.container} d-flex overflow-auto pointer-event-on pl-3 pr-3 pb-3 pt-3 mb-3 no-scrollbar`}
      style={springProps}
      ref={poiCardContainer}
    >
      {getVisiblePOICards()}
    </a.div>
  );
}

/**
 * Eine kleine Card (wie visitenKarte), die ein Feature auswählen kann
 * oder sogar eine InfoCard hochfahren lassen kann beim Klick
 * @param onClick
 * @param children - Children in diesem Fall heißt, ob dieser POI keinen Standort aber dafür eben children hat
 * @param feature
 * @param headline
 * @param text
 * @param selected
 * @returns {JSX.Element}
 * @constructor
 */
function PreviewPoiCard({
  children,
  onDivClick,
  onInfoClick,
  feature,
  icon,
  headline,
  text,
  selected,
}) {
  const intl = useIntl();
  let ariaLabel;
  ariaLabel = intl.formatMessage(
    {
      id: selected
        ? "search.screenreader.poiCard.onMap.active"
        : "search.screenreader.poiCard.onMap.inactive",
    },
    {
      poiName: headline,
      poiAddress: text,
    }
  );
  return (
    <StandardCard
      role={"button"}
      className={`standardCard--poiCard ${
        selected
          ? "standardCard--poiCard--selected"
          : "standardCard--poiCard--unselected"
      } d-flex flex-column p-3 text-left mr-2 col-8 col-md-3 ${styles.poiCard}`}
      ariaLabel={ariaLabel}
      onClick={() => {
        // falls bereits selected ist es einfacher für den user einfach auf die karte zu klicken anstatt extra
        // auf mehr infos
        if (selected) {
          onInfoClick(feature);
        } else {
          onDivClick(feature);
        }
      }}
    >
      <div className="d-flex align-items-center">
        {children ? (
          <FontAwesomeIcon
            icon={"map-marker-alt"}
            color={CUSTOMIZABLE_VALUES.childMarker}
            className={"mr-2 mb-2"}
          />
        ) : null}
        {icon ? <FontAwesomeIcon icon={icon} className={"mr-2 mb-2"} /> : null}
        <h4
          className={`${
            selected
              ? "standardCard--poiCard--selected__text"
              : "standardCard--poiCard--unselected__text text-break"
          } text-overflow-ellipsis`}
        >
          {headline}
        </h4>
      </div>
      <span
        className={`${
          selected
            ? "standardCard--poiCard--selected__text"
            : "standardCard--poiCard--unselected__text"
        } text-overflow-ellipsis mb-3`}
      >
        {text}
      </span>
      <div className={"d-flex"}>
        <StandardButton
          handleClick={() => onInfoClick(feature)}
          buttonClasses={`mt-auto normalButton--primary__poiCard ml-auto
          ${
            selected
              ? "normalButton--primary__poiCard--selected"
              : "normalButton--primary__poiCard--unselected"
          }`}
        >
          <FormattedMessage id="infoCard.moreInformation" />
        </StandardButton>
      </div>
    </StandardCard>
  );
}
